import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { Box } from '@mui/material';
import {
  AnalyticsEventName,
  Focus,
  GetWeeklyForecastRequestFilters,
} from '@provider-portal/common/api/gen/CampaignDeliveryProvider';
import { Spinner } from '@provider-portal/common/components/Spinner';
import { performNavigate } from '@provider-portal/common/utils/navigation';
import {
  FOCUS_KEY_TO_MIXPANEL_EVENT_MAPPING,
  PromotionsAnalyticsEvents,
} from '@provider-portal/promotions-common/analyticsEvents';
import { SmartPromotionsBanner } from '@provider-portal/promotions-common/components/SmartPromotionsBanner/SmartPromotionsBanner';
import { usePromotionSettingsLoader } from '@provider-portal/promotions-common/hooks/usePromotionSettingsLoader';
import { usePromotionsLoader } from '@provider-portal/promotions-common/hooks/usePromotionsLoader';
import { SetupNewPromotion } from 'promotions/components';
import { ActivePromotionsGrid } from 'promotions/components/ActivePromotionsGrid/ActivePromotionsGrid';
import { PromotionTable } from 'promotions/components/PromotionTable/PromotionTable';

import { useSelectedProvider, useSelectedProviderId } from 'hooks/useSelectedProvider';

import { useCommonAppServices } from 'root/CommonAppServicesContext';

import { usePromotionsApiFetcher } from '../../providers/apiClient/PromotionsApiFetchersProvider';
import { routes } from '../../routes';

export const ListPromotions = () => {
  const intl = useIntl();
  const providerId = useSelectedProviderId();

  const listFetcher = usePromotionsApiFetcher().list.useFetcher();
  const permissionsFetcher = usePromotionsApiFetcher().fetchPromotionsPermissions.useFetcher();
  const recommendationsAndFocusesFetcher =
    usePromotionsApiFetcher().availableRecommendationsAndFocuses.useFetcher();
  const analyticsFetcher = usePromotionsApiFetcher().storeAnalyticsEvent.useFetcher();

  const [focuses, setFocuses] = useState<Focus[]>([]);
  const [recommendations, setRecommendations] = useState<Focus[]>([]);

  const historyTableName = intl.formatMessage({
    id: 'components.assignedPromotions.promotionTableName.history',
  });

  const promotions = usePromotionsLoader(providerId, listFetcher);
  const selectedProvider = useSelectedProvider();
  const isMetricsBanned = !selectedProvider.permissions.canCreatePromotions;
  const isAbleToCreatePromotion = selectedProvider.permissions.selfAssignCampaigns;
  const shouldSeeSmartPromotions = selectedProvider.permissions.canSeeSmartPromotions;
  const promotionSettings = usePromotionSettingsLoader(providerId, permissionsFetcher);

  useEffect(() => {
    if (!providerId) {
      return;
    }

    analyticsFetcher
      .fetch({
        event: {
          type: AnalyticsEventName.PROMOTION_SECTION_VISITED,
        },
      })
      .finally(() => {});
  }, [providerId, analyticsFetcher]);

  const history = useHistory();
  const goToPromotion = useCallback(
    (id: number) => performNavigate(history, routes.details.index, { promotionId: id }),
    [history],
  );
  const { mixpanelService } = useCommonAppServices();

  useEffect(() => {
    if (!providerId || isMetricsBanned || !isAbleToCreatePromotion) {
      return;
    }
    recommendationsAndFocusesFetcher
      .fetch({ provider_id: providerId, ignore_focuses: false })
      .then((data) => {
        setFocuses(data.focuses);
        setRecommendations(data.recommendations);
      });
  }, [providerId, recommendationsAndFocusesFetcher, isMetricsBanned, isAbleToCreatePromotion]);

  const openUseSelectPromotionWizard = useCallback(
    (filters: GetWeeklyForecastRequestFilters, key: string) => {
      mixpanelService.trackProviderEvent(FOCUS_KEY_TO_MIXPANEL_EVENT_MAPPING[key], {
        providerId,
      });
      const prefill = btoa(JSON.stringify(filters));
      const urlPath = `${routes.addPromotion}?${filters ? `prefill=${prefill}` : ''}`;
      history.push(urlPath);
    },
    [history, providerId, mixpanelService],
  );

  const onSmartPromotionsBannerClick = useCallback(() => {
    mixpanelService.trackProviderEvent(
      PromotionsAnalyticsEvents.promotions_list_smart_promotions_banner_clicked,
      {
        providerId,
        cityId: selectedProvider.cityId,
      },
    );
  }, [mixpanelService, providerId, selectedProvider.cityId]);

  if (listFetcher.isFetching || !promotions) {
    return (
      <Box textAlign="center">
        <Spinner />
      </Box>
    );
  }

  return (
    <>
      <Box>
        {/* TODO: https://taxify.atlassian.net/browse/CAMLE-6287 */}
        {isAbleToCreatePromotion && (
          <Box mt={5} maxWidth="xl">
            <ActivePromotionsGrid
              promotionDetails={promotions.active}
              promotionClicked={goToPromotion}
              lastUpdate={undefined}
              isUpdatedByOrder={false}
              shouldShowBannerIfNoActivePromotions
            />
          </Box>
        )}
        {shouldSeeSmartPromotions && (
          <Box mt={5} maxWidth="xl">
            <SmartPromotionsBanner
              netRevenueUplift={promotionSettings.net_revenue_uplift}
              shouldRedirectToScheduler={promotionSettings.should_skip_landing_page}
              onButtonClick={onSmartPromotionsBannerClick}
            />
          </Box>
        )}
        {isAbleToCreatePromotion && (
          <Box mt={5} maxWidth="xl">
            <SetupNewPromotion
              onClick={openUseSelectPromotionWizard}
              focuses={focuses}
              recommendations={recommendations}
            />
          </Box>
        )}
        {promotions.ended.length > 0 ? (
          <Box maxWidth="xl">
            <PromotionTable
              promotionDetailList={promotions.ended}
              promotionClicked={goToPromotion}
              tableName={historyTableName}
              showPagination
            />
          </Box>
        ) : null}
      </Box>
    </>
  );
};
